import React from 'react';
import { ViewStyle } from 'react-native';
import Toast from 'react-native-toast-message';

import { useGetNonProfitInsights, useNonProfitOrgQrCode } from '@/api/hooks/nonProfitOrgHooks';
import BrandedButton from '@/components/buttons/BrandedButton';
import ShareQrCard from '@/components/ShareQrCard';
import { useAuth } from '@/contexts/authContext';
import { useInfoModal } from '@/contexts/infoModalContext';
import { createShortDeepLinkApiBody } from '@/utils/createDeepLink';
import { firebaseImage, safeJsonParseArray, ThumbSize } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';

interface GenerateNPOQRButtonProps {
  title: string;
  style?: ViewStyle;
}

const GenerateNPOQRButton: React.FC<GenerateNPOQRButtonProps> = ({ title, style }) => {
  const { state } = useAuth();
  const { showInfo } = useInfoModal();
  const { mutateAsync: generateQr, isPending } = useNonProfitOrgQrCode();

  const {
    data: npoData,
    isPending: npoDataPending,
    isLoading: npoDataLoading
  } = useGetNonProfitInsights({ id: state.user?.user?.nonProfitOrgId || '' });

  const successTitle = i18n.t('end_flow.success_title');
  const successMessage = i18n.t('settings.share_qr.success_message');

  const onCreateQr = async () => {
    try {
      const npoId = state.user?.user?.nonProfitOrgId;
      if (!npoId || npoDataLoading || npoDataPending) return;

      const route = `/npoDetail/${npoId}/insights`;
      const appsflyerBody = await createShortDeepLinkApiBody(
        route,
        npoData?.thumbnail && npoData.thumbnail !== '[]' && npoData.thumbnail.length > 0
          ? firebaseImage(safeJsonParseArray(npoData?.thumbnail ?? '[]')?.at(0), ThumbSize.Small)
          : undefined,
        npoData?.name ?? ''
      );

      if (!appsflyerBody) {
        Toast.show({
          type: 'error',
          text1: 'Error',
          text2: 'Could not generate deep link'
        });
        return;
      }

      await generateQr(
        { appsflyerBody: appsflyerBody },
        {
          onSuccess: (data) => {
            if (data?.qrCode) {
              showInfo({
                type: 'full',
                title: successTitle,
                message: successMessage,
                showDoneButton: false,
                accessoryView: (
                  <ShareQrCard
                    npoName={npoData?.name ?? ''}
                    thumbnail={npoData?.thumbnail}
                    qrUrl={data.qrCode}
                    shortLink={data.shortLink}
                  />
                )
              });
            }
          }
        }
      );
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <BrandedButton style={style} loading={isPending} onPress={onCreateQr}>
      {title}
    </BrandedButton>
  );
};

export default GenerateNPOQRButton;
